import { useState } from "react";
import logo from "../../assets/logo.png";
import { setLink, useLink } from "../../utils/ClickedLink";
import { useNavigate } from "react-router-dom";
import { Menu, X, Search, ChevronDown, Mountain, Calendar, ArrowRight, XIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";


const NavigationComponent = () => {
  const [currentLink, setCurrentLink] = useState(useLink());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [bannerVisible, setBannerVisible] = useState(true);
  const navigate = useNavigate();

  const handleLinkClick = (link, path) => {
    setLink(link);
    setCurrentLink(link);
    if (path) navigate(path);
    setMobileMenuOpen(false); // close menu on mobile
    setCountryDropdownOpen(false); // close dropdown
  };

  const handleSendEmail = () => {
    const to = "info@mambahikers.com";
    const subject = encodeURIComponent("Hello from My App");
    const body = encodeURIComponent(
      "Hi there,\n\nI wanted to reach out to you..."
    );

    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${to}`;

    window.open(gmailUrl, "_blank");
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?text=${encodeURIComponent(searchQuery.trim())}`);
      setMobileMenuOpen(false);
    }
  };

  const countries = [
    { label: "Kenya", value: "Kenya" },
    { label: "Tanzania", value: "Tanzania" },
    { label: "Uganda", value: "Uganda" },
    { label: "Rwanda", value: "Rwanda" },
  ];

  return (
    <>
      {/* Year Update Banner */}
      {bannerVisible && <div className="h-[52px]" />} {/* Spacer for fixed banner */}
      <AnimatePresence>
        {bannerVisible && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full bg-gradient-to-r from-mamba-navy via-mamba-sky-blue to-mamba-navy overflow-hidden fixed top-0 left-0 right-0 z-[60]"
          >
            <div className="relative px-4 py-3 flex items-center justify-center gap-3">
              {/* Left decoration */}
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="hidden md:block"
              >
                <Calendar className="w-5 h-5 text-mamba-golden" />
              </motion.div>

              {/* Main content */}
              <a
                href="https://drive.google.com/file/d/1YSbUgG29UcFylP6ciBG9VM8im7dYFwTi/view"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 md:gap-3 group"
              >
                <span className="text-white font-montserrat text-sm md:text-base font-bold">
                  🎉 Check out our 2026 Calendar!
                </span>
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-mamba-golden group-hover:text-white transition-colors" />
                </motion.div>
              </a>

              {/* Right decoration */}
              <motion.div
                animate={{ rotate: [0, -360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="hidden md:block"
              >
                <Calendar className="w-5 h-5 text-mamba-golden" />
              </motion.div>

              {/* Close button */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setBannerVisible(false)}
                className="absolute right-2 md:right-4 p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                aria-label="Close banner"
              >
                <X className="w-4 h-4 text-white" />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation */}
      <nav 
        className="w-full px-4 md:px-8 lg:px-16 xl:px-24 py-4 font-semibold bg-white/95 backdrop-blur-md shadow-lg border-b-2 border-mamba-golden/20 sticky top-0 z-50 transition-all duration-300"
      >
      <div className="flex justify-between items-center gap-4 max-w-[1800px] mx-auto">
        {/* Left Section: Logo + Navigation Links */}
        <div className="flex items-center gap-8 lg:gap-12">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="h-14 md:h-16 cursor-pointer flex-shrink-0"
            onClick={() => handleLinkClick("home", "/")}
          >
            <img src={logo} alt="logo" className="h-full drop-shadow-md" />
          </motion.div>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center gap-1 xl:gap-2">
            <motion.div
              whileHover={{ y: -2 }}
              onClick={() => handleLinkClick("events", "/events")}
              className={`px-4 py-2 rounded-xl font-montserrat cursor-pointer transition-all ${
                currentLink === "events" 
                  ? "bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white font-bold shadow-md" 
                  : "text-slate-700 hover:bg-mamba-bg-light hover:text-mamba-navy"
              }`}
            >
              Events
            </motion.div>
            <motion.div
              whileHover={{ y: -2 }}
              onClick={() => handleLinkClick("programmes", "/programmes")}
              className={`px-4 py-2 rounded-xl font-montserrat cursor-pointer transition-all ${
                currentLink === "programmes" 
                  ? "bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white font-bold shadow-md" 
                  : "text-slate-700 hover:bg-mamba-bg-light hover:text-mamba-navy"
              }`}
            >
              Programs
            </motion.div>

            {/* Countries Dropdown */}
            <div
              onMouseEnter={() => setCountryDropdownOpen(true)}
              onMouseLeave={() => setCountryDropdownOpen(false)}
              className="relative"
            >
              <motion.div
                whileHover={{ y: -2 }}
                className={`flex items-center gap-1 px-4 py-2 rounded-xl font-montserrat cursor-pointer transition-all ${
                  currentLink === "destinations" 
                    ? "bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white font-bold shadow-md" 
                    : "text-slate-700 hover:bg-mamba-bg-light hover:text-mamba-navy"
                }`}
              >
                <span>Countries</span>
                <ChevronDown
                  size={18}
                  className={`transition-transform ${
                    countryDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </motion.div>
              <AnimatePresence>
                {countryDropdownOpen && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 mt-2 w-56 z-[100]"
                  >
                    <div className="bg-white shadow-2xl rounded-2xl border-2 border-mamba-golden/30 overflow-hidden">
                    {countries.map((country, idx) => (
                      <motion.div
                        key={country.value}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        onClick={() =>
                          handleLinkClick(
                            "destinations",
                            `/country/destinations/${country.value.toLowerCase()}`
                          )
                        }
                        className="px-5 py-3 font-inter hover:bg-gradient-to-r hover:from-mamba-bg-light hover:to-mamba-sky-blue/10 hover:text-mamba-navy transition-all text-base cursor-pointer flex items-center gap-3 border-b border-slate-100 last:border-b-0"
                      >
                        <Mountain size={16} className="text-mamba-sky-blue" />
                        {country.label}
                      </motion.div>
                    ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <motion.div
              whileHover={{ y: -2 }}
              onClick={() => handleLinkClick("about-us", "/about-us")}
              className={`px-4 py-2 rounded-xl font-montserrat cursor-pointer transition-all ${
                currentLink === "about-us" 
                  ? "bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white font-bold shadow-md" 
                  : "text-slate-700 hover:bg-mamba-bg-light hover:text-mamba-navy"
              }`}
            >
              About Us
            </motion.div>
            <motion.div 
              whileHover={{ y: -2 }}
              className="px-4 py-2 rounded-xl font-montserrat cursor-pointer transition-all text-slate-700 hover:bg-mamba-bg-light hover:text-mamba-navy"
            >
              <a
                href="https://www.instagram.com/mambahikers_gear_shop?igsh=MThtc28xdGNuaWNqcw=="
                target="_blank"
                rel="noopener noreferrer"
              >
                Shop With Us
              </a>
            </motion.div>
            <motion.div
              whileHover={{ y: -2 }}
              onClick={() => handleLinkClick("contact-us", "/contact-us")}
              className={`px-4 py-2 rounded-xl font-montserrat cursor-pointer transition-all ${
                currentLink === "contact-us" 
                  ? "bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white font-bold shadow-md" 
                  : "text-slate-700 hover:bg-mamba-bg-light hover:text-mamba-navy"
              }`}
            >
              Contact Us
            </motion.div>
          </div>
        </div>

        {/* Right Section: Search Input */}
        <div className="hidden lg:flex items-center">
          <form onSubmit={handleSearchSubmit} className="relative group">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="relative"
            >
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-mamba-navy/40 w-5 h-5" />
              <input
                type="text"
                placeholder="Search adventures..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 xl:w-80 pl-12 pr-4 py-3 rounded-2xl border-2 border-mamba-golden/30 focus:border-mamba-golden focus:ring-2 focus:ring-mamba-golden/20 focus:outline-none transition-all placeholder:text-slate-400 text-sm font-inter shadow-md bg-gradient-to-r from-white to-mamba-bg-light"
              />
              <motion.button 
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                type="submit" 
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy p-2.5 rounded-xl hover:shadow-xl transition-all"
              >
                <Search className="w-4 h-4" />
              </motion.button>
            </motion.div>
          </form>
        </div>

        {/* Mobile Menu Button */}
        <motion.div 
          whileTap={{ scale: 0.9 }}
          className="lg:hidden cursor-pointer"
        >
          {mobileMenuOpen ? (
            <div className="p-2 rounded-xl bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white shadow-lg">
              <X size={24} onClick={() => setMobileMenuOpen(false)} />
            </div>
          ) : (
            <div className="p-2 rounded-xl bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy shadow-md">
              <Menu size={24} onClick={() => setMobileMenuOpen(true)} />
            </div>
          )}
        </motion.div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden mt-6 pb-4 overflow-hidden"
          >
            {/* Mobile Search */}
            <div className="mb-6 px-2">
              <form onSubmit={handleSearchSubmit} className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-mamba-navy/40 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search adventures..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-2xl border-2 border-mamba-golden/30 focus:border-mamba-golden focus:ring-2 focus:ring-mamba-golden/20 focus:outline-none transition-all placeholder:text-slate-400 text-sm font-inter shadow-md bg-gradient-to-r from-white to-mamba-bg-light"
                />
                <button 
                  type="submit" 
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy p-2.5 rounded-xl hover:shadow-xl transition-all"
                >
                  <Search className="w-4 h-4" />
                </button>
              </form>
            </div>

            {/* Mobile Navigation Links */}
            <div className="flex flex-col space-y-3 px-2">
              <motion.div
                whileTap={{ scale: 0.98 }}
                onClick={() => handleLinkClick("events", "/events")}
                className={`py-3 px-4 rounded-xl transition-all font-montserrat text-center shadow-sm ${
                  currentLink === "events" 
                    ? "bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white font-bold shadow-md" 
                    : "bg-white text-slate-700 hover:bg-mamba-bg-light border border-mamba-navy/10"
                }`}
              >
                Events
              </motion.div>
              <motion.div
                whileTap={{ scale: 0.98 }}
                onClick={() => handleLinkClick("programmes", "/programmes")}
                className={`py-3 px-4 rounded-xl transition-all font-montserrat text-center shadow-sm ${
                  currentLink === "programmes" 
                    ? "bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white font-bold shadow-md" 
                    : "bg-white text-slate-700 hover:bg-mamba-bg-light border border-mamba-navy/10"
                }`}
              >
                Programs
              </motion.div>

              {/* Mobile Countries Section */}
              <div className="bg-gradient-to-br from-white to-mamba-bg-light rounded-2xl p-4 shadow-sm border border-mamba-golden/20">
                <div className="font-bold font-montserrat text-mamba-navy mb-3 flex items-center gap-2">
                  <Mountain size={18} className="text-mamba-sky-blue" />
                  Countries
                </div>
                <div className="flex flex-col space-y-2">
                  {countries.map((country, idx) => (
                    <motion.div
                      key={country.value}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() =>
                        handleLinkClick(
                          "destinations",
                          `/country/destinations/${country.value.toLowerCase()}`
                        )
                      }
                      className="px-4 py-2 font-inter bg-white hover:bg-mamba-sky-blue/10 hover:text-mamba-navy transition-all rounded-lg border border-slate-100 shadow-sm"
                    >
                      {country.label}
                    </motion.div>
                  ))}
                </div>
              </div>

              <motion.div
                whileTap={{ scale: 0.98 }}
                onClick={() => handleLinkClick("about-us", "/about-us")}
                className={`py-3 px-4 rounded-xl transition-all font-montserrat text-center shadow-sm ${
                  currentLink === "about-us" 
                    ? "bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white font-bold shadow-md" 
                    : "bg-white text-slate-700 hover:bg-mamba-bg-light border border-mamba-navy/10"
                }`}
              >
                About Us
              </motion.div>
              <motion.div 
                whileTap={{ scale: 0.98 }}
                className="py-3 px-4 rounded-xl transition-all font-montserrat text-center shadow-sm bg-white text-slate-700 hover:bg-mamba-bg-light border border-mamba-navy/10"
              >
                <a
                  href="https://www.instagram.com/mambahikers_gear_shop?igsh=MThtc28xdGNuaWNqcw=="
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Shop With Us
                </a>
              </motion.div>
              <motion.div
                whileTap={{ scale: 0.98 }}
                onClick={() => handleLinkClick("contact-us", "/contact-us")}
                className={`py-3 px-4 rounded-xl transition-all font-montserrat text-center shadow-sm ${
                  currentLink === "contact-us" 
                    ? "bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white font-bold shadow-md" 
                    : "bg-white text-slate-700 hover:bg-mamba-bg-light border border-mamba-navy/10"
                }`}
              >
                Contact Us
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
    </>
  );
};

export default NavigationComponent;
